import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Container,
  Paper,
  Fade
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { validateUniqueFields, sendEmailOTP, register } from '../../api/authApi';
import EmailVerifyDialog from './EmailVerifyDialog';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import AuthBlueprintHero, { AuthBlueprintPanel } from './AuthBlueprintHero';

// Your original theme color
const themeColor = 'rgb(57, 170, 252)'; // #39aafc

const registrationSchema = Yup.object().shape({
  companyname: Yup.string()
    .required('Company name is required')
    .min(2, 'Company name must be at least 2 characters'),
  fullname: Yup.string()
    .required('User name is required')
    .min(2, 'User name must be at least 2 characters'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  mobile: Yup.string()
    .required('Mobile number is required')
    .matches(/^[0-9]{10}$/, 'Must be exactly 10 digits'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, 'Must contain at least one special character'),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password')], 'Passwords must match')
});

// Clean MUI input styles with Roboto font
const inputFieldStyles = {
  mb: 2.5,
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Roboto, sans-serif',
    backgroundColor: '#fff',
    borderRadius: '14px',
    transition: 'box-shadow 0.25s ease',
    '& fieldset': {
      borderColor: '#e3e8ef',
    },
    '&:hover fieldset': {
      borderColor: themeColor,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 4px ${themeColor}1f`,
    },
    '&.Mui-focused fieldset': {
      borderColor: themeColor,
      borderWidth: '1.5px',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '16px', // 16px prevents iOS from auto-zooming on focus
    padding: '15px 14px',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Roboto, sans-serif',
    '&.Mui-focused': {
      color: themeColor,
    },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Roboto, sans-serif',
  },
};

// Shared gradient primary button (tactile press + glow)
const primaryButtonSx = {
  py: 1.6,
  fontFamily: 'Roboto, sans-serif',
  fontSize: '16px',
  fontWeight: 700,
  mb: 2,
  mt: 1,
  borderRadius: '14px',
  color: '#fff',
  textTransform: 'none',
  background: `linear-gradient(135deg, ${themeColor} 0%, #1565C0 100%)`,
  boxShadow: `0 10px 24px ${themeColor}59`,
  transition: 'transform 0.15s ease, box-shadow 0.25s ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${themeColor} 0%, #1565C0 100%)`,
    boxShadow: `0 12px 30px ${themeColor}80`,
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
  '&.Mui-disabled': {
    color: '#fff',
    opacity: 0.75,
  },
};

const SignupPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showEmailVerify, setShowEmailVerify] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [isPasswordFieldActive, setIsPasswordFieldActive] = useState(false);

  const togglePassword = () => setShowPassword(!showPassword);

  const handleSubmit = async (values, { setFieldError }) => {
    try {
      setSubmitting(true);
      setError(null);

      // Step 1: Validate unique fields
      try {
        await validateUniqueFields({
          email: values.email,
          mobile: values.mobile
        });
      } catch (err) {
        if (err.errors) {
          if (err.errors.email) {
            setFieldError('email', err.errors.email);
          }
          if (err.errors.mobile) {
            setFieldError('mobile', err.errors.mobile);
          }
          return;
        }
        throw err;
      }

      // Step 2: Send email OTP
      await sendEmailOTP({
        email: values.email,
        fullname: values.fullname,
        purpose: 'registration'
      });

      // Store form values for later
      setFormValues(values);

      // Show email verification dialog
      setShowEmailVerify(true);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // After email OTP is verified
  const handleEmailVerified = async () => {
    try {
      setSubmitting(true);
      setError(null);

      // Register user
      const response = await register(formValues);

      if (response.success) {
        setShowEmailVerify(false);
        setSuccessMessage('Registration successful! Redirecting to login...');

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      setShowEmailVerify(false);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 3 },
        pt: { xs: 'max(16px, env(safe-area-inset-top))', sm: 3 },
        pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 3 },
        background: {
          xs: 'linear-gradient(160deg, #081b3a 0%, #0d2b5c 55%, #123a75 100%)',
          sm: 'linear-gradient(160deg, #0d47a1 0%, #1565C0 32%, #42A5F5 68%, #90CAF9 100%)',
        },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-15%',
          left: '-20%',
          width: '70vw',
          height: '70vw',
          maxWidth: '520px',
          maxHeight: '520px',
          borderRadius: '50%',
          background: { xs: 'radial-gradient(circle, rgba(77,178,252,0.18) 0%, rgba(66,165,245,0) 70%)', sm: 'radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(66,165,245,0) 70%)' },
          filter: 'blur(20px)',
          animation: 'floatA 14s ease-in-out infinite',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-20%',
          right: '-15%',
          width: '80vw',
          height: '80vw',
          maxWidth: '560px',
          maxHeight: '560px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(13,71,161,0.45) 0%, rgba(13,71,161,0) 70%)',
          filter: 'blur(20px)',
          animation: 'floatB 18s ease-in-out infinite',
        },
        '@keyframes floatA': {
          '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(30px, 40px) scale(1.08)' },
        },
        '@keyframes floatB': {
          '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(-40px, -30px) scale(1.12)' },
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: { xs: 1, sm: 4 }, px: { xs: 0, sm: 2 }, maxWidth: { md: '960px' } }}>
        <Fade in={true} timeout={800}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: { xs: '28px', sm: '24px' },
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.6)',
              boxShadow: '0 24px 70px rgba(8, 47, 121, 0.35), 0 2px 8px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
              <Box sx={{ display: { xs: 'none', md: 'block' }, width: '42%', flexShrink: 0 }}>
                <AuthBlueprintPanel tagline="Create Your Account" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Logo Section */}
            {isMobile && <AuthBlueprintHero tagline="Create Your Account" />}
            <Box
              sx={{
                pt: { xs: isMobile ? 3 : 4, sm: 5 },
                pb: 3,
                px: { xs: 3, sm: 4 },
                textAlign: 'center',
                background: `linear-gradient(135deg, ${themeColor}08 0%, transparent 100%)`,
              }}
            >

              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  mb: 1,
                  fontSize: { xs: '24px', sm: '28px' },
                }}
              >
                Create An Account
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  color: '#666',
                  fontSize: '15px',
                }}
              >
                Sign up to get started with ARCDX
              </Typography>
            </Box>

            {/* Form Section */}
            <Box sx={{ px: { xs: 3, sm: 4 }, pb: { xs: 4, sm: 5 } }}>
              {/* Success Message */}
              {successMessage && (
                <Alert
                  severity="success"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    border: '1px solid #4caf5020',
                  }}
                >
                  {successMessage}
                </Alert>
              )}

              {/* Error Message */}
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    border: '1px solid #f4433620',
                  }}
                >
                  {error}
                </Alert>
              )}

              <Formik
                initialValues={{
                  companyname: '',
                  fullname: '',
                  email: '',
                  mobile: '',
                  password: '',
                  confirmPassword: ''
                }}
                validationSchema={registrationSchema}
                onSubmit={handleSubmit}
              >
                {({ errors, touched, values }) => (
                  <Form>
                    <Field name="companyname">
                      {({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Company Name"
                          error={touched.companyname && Boolean(errors.companyname)}
                          helperText={touched.companyname && errors.companyname}
                          sx={inputFieldStyles}
                        />
                      )}
                    </Field>

                    <Field name="fullname">
                      {({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="User Name"
                          error={touched.fullname && Boolean(errors.fullname)}
                          helperText={touched.fullname && errors.fullname}
                          sx={inputFieldStyles}
                        />
                      )}
                    </Field>

                    <Field name="email">
                      {({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Email Address"
                          type="email"
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          sx={inputFieldStyles}
                        />
                      )}
                    </Field>

                    <Field name="mobile">
                      {({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Mobile Number"
                          error={touched.mobile && Boolean(errors.mobile)}
                          helperText={touched.mobile && errors.mobile}
                          sx={inputFieldStyles}
                          inputProps={{
                            maxLength: 10
                          }}
                        />
                      )}
                    </Field>

                    <Field name="password">
                      {({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Password"
                          type={showPassword ? 'text' : 'password'}
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          onFocus={() => setIsPasswordFieldActive(true)}
                          onBlur={() => setIsPasswordFieldActive(false)}
                          sx={inputFieldStyles}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={togglePassword}
                                  edge="end"
                                  sx={{ color: '#666' }}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    </Field>

                    <PasswordStrengthIndicator
                      password={values.password}
                      isActive={isPasswordFieldActive}
                    />

                    <Field name="confirmPassword">
                      {({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Confirm Password"
                          type={showPassword ? 'text' : 'password'}
                          error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                          helperText={touched.confirmPassword && errors.confirmPassword}
                          sx={inputFieldStyles}
                        />
                      )}
                    </Field>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={submitting}
                      sx={primaryButtonSx}
                    >
                      {submitting ? (
                        <CircularProgress size={24} sx={{ color: 'white' }} />
                      ) : (
                        'Create Account'
                      )}
                    </Button>

                    <Box
                      sx={{
                        textAlign: 'center',
                        mt: 3,
                        pt: 3,
                        borderTop: '1px solid #e0e0e0',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', color: '#666', fontSize: '14px' }}>
                        Already have an account?{' '}
                        <Link
                          component={RouterLink}
                          to="/login"
                          sx={{
                            fontFamily: 'Roboto, sans-serif',
                            color: themeColor,
                            fontWeight: 700,
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Sign In
                        </Link>
                      </Typography>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
              </Box>
            </Box>
          </Paper>
        </Fade>

        {/* Footer Text */}
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            textAlign: 'center',
            mt: 3,
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '13px',
          }}
        >
          © {new Date().getFullYear()} DigiBooks. All rights reserved.
        </Typography>
      </Container>

      {/* Email OTP Verification Dialog */}
      <EmailVerifyDialog
        open={showEmailVerify}
        onClose={() => setShowEmailVerify(false)}
        email={formValues?.email}
        fullname={formValues?.fullname}
        purpose="registration"
        onVerifySuccess={handleEmailVerified}
        title="Verify Your Email"
        description="Please enter the 6-digit verification code sent to your email to complete registration."
      />
    </Box>
  );
};

export default SignupPage;
